import { colors } from "@indeed/ifl-tokens";
import { borderRadius, spacing } from "@indeed/ifl-tokens/react-native";
import React, { useEffect, useRef } from "react";
import { StyleSheet } from "react-native";

import { Box, Flex, Text, type TextRef } from "../../../../../../../../mads";
import { useAccessibilityFocus } from "../../../../../../hooks/useAccessibilityFocus";
import { useFullScreenVJActive } from "../../../../../../hooks/useFullScreenVJActive";
import type InlineQuestionResponse from "../../../types/InlineQuestionResponse";
import { QuestionText } from "../QuestionText";
import { InlineResponseButtons } from "./InlineResponseButtons";

export const getFieldsetStyleOverrides = (_isLegendInline: boolean) =>
  StyleSheet.create({
    override: {
      borderWidth: 0,
      marginInline: 0,
      paddingInline: 0,
      paddingBlock: 0,
    },
  }).override;

export interface InlineQuestionProps {
  label: string;
  onSubmit: (r: InlineQuestionResponse) => void;
}

export const InlineQuestion: React.FunctionComponent<InlineQuestionProps> = ({
  label,
  onSubmit,
}) => {
  const focus = useAccessibilityFocus();
  const questionTextRef = useRef<TextRef | null>(null);
  const isFirstRender = useRef(true);
  const isFullScreenVJActive = useFullScreenVJActive();

  useEffect(() => {
    // Skip focus on initial render - only focus when label changes
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Move screen reader focus to the question text with screen reader after
    // the response is submitted, and the label is updated (next question)
    if (label && questionTextRef.current) {
      return focus(questionTextRef.current);
    }
  }, [label, focus]);

  if (isFullScreenVJActive) {
    return (
      <Box sx={fullscreenVJStyles.container}>
        <Box sx={fullscreenVJStyles.contentWrapper}>
          <Text ref={questionTextRef} accessible={true} sx={fullscreenVJStyles.questionText}>
            <QuestionText>{label}</QuestionText>
          </Text>
        </Box>
        <Box sx={fullscreenVJStyles.divider} />
        <InlineResponseButtons label={label} onClick={onSubmit} />
      </Box>
    );
  }

  return (
    <Flex
      sx={{
        justifyContent: "space-between",
        paddingVertical: 8,
        gap: 8,
        paddingRight: 24,
      }}
    >
      <Text
        ref={questionTextRef}
        accessible={true}
        sx={{
          flexGrow: 1,
          flexWrap: "wrap",
          flexShrink: 1,
          color: colors.neutral[800],
        }}
      >
        <QuestionText>{label}</QuestionText>
      </Text>
      <InlineResponseButtons label={label} onClick={onSubmit} />
    </Flex>
  );
};

const fullscreenVJStyles = StyleSheet.create({
  container: {
    borderRadius: borderRadius.lg,
    backgroundColor: "white",
    overflow: "hidden",
    borderWidth: 1,
    borderColor: colors.neutral[300],
  },
  contentWrapper: {
    padding: spacing.s4,
  },
  questionText: {
    color: colors.neutral[800],
  },
  divider: {
    height: 1,
    backgroundColor: colors.neutral[300],
  },
});
